// Pure cart pricing math, extracted from MyCart.jsx so the discount/total
// calculation (a money path) is unit-testable without rendering the component.

export function calculateRawTotal(cartItems) {
  return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export function calculateDiscountAmount(rawTotal, discountData) {
  if (!discountData) return 0;
  if (discountData.discountType === 'percent') {
    return Math.round(rawTotal * discountData.discountValue / 100);
  }
  return Math.min(discountData.discountValue, rawTotal);
}

export function calculateTotalPrice(rawTotal, discountAmount) {
  return Math.max(0, rawTotal - discountAmount);
}

// Mirrors backend/utils/pricing.js's validateDiscount - same eligibility
// rules must hold client-side (for the UI message) and server-side (for the
// actual charge), so a discount code doesn't pass locally but reject on payment.
export function validateDiscount(discountDoc, rawTotal) {
  if (!discountDoc) return { valid: false, error: 'Koden finnes ikke.' };
  const d = discountDoc;

  if (!d.isActive) return { valid: false, error: 'Koden er ikke aktiv.' };

  if (d.expiryDate && !d.noExpiry) {
    const [y, m, day] = d.expiryDate.split('-');
    if (new Date(y, m - 1, day, 23, 59, 59) < new Date()) {
      return { valid: false, error: 'Koden har utløpt.' };
    }
  }

  if (d.maxUses !== null && d.maxUses !== undefined && (d.usedCount || 0) >= d.maxUses) {
    return { valid: false, error: 'Koden har nådd maks antall bruk.' };
  }

  if (d.minOrderValue && rawTotal < d.minOrderValue) {
    return { valid: false, error: `Minimum bestillingsverdi er ${d.minOrderValue} kr for denne koden.` };
  }

  return { valid: true, data: d };
}

// Adapts a discount_codes row (snake_case Postgres columns) to the shape
// validateDiscount expects - mirrors backend/utils/pricing.js's
// discountRowToValidationShape exactly, since both must agree on eligibility.
export function discountRowToValidationShape(row) {
  if (!row) return null;
  return {
    id: row.id,
    isActive: row.active,
    discountType: row.type,
    discountValue: Number(row.value),
    minOrderValue: row.min_total,
    maxUses: row.max_uses,
    usedCount: row.uses_count,
    noExpiry: row.no_expiry,
    expiryDate: row.expires_at ? new Date(row.expires_at).toISOString().slice(0, 10) : null,
  };
}

// A klippekort card is "active" (counts toward the cap) once it has stamps left -
// mirrors the exact definition KlippekortRedeem.jsx uses to build its own list.
export function isActiveKlippekort(item) {
  const stampTotal = item.stampTotal || item.stampAmounts || 0;
  const stampsLeft = typeof item.stampsLeft === "number" ? item.stampsLeft : (stampTotal - (item.stampUsed || 0));
  return stampsLeft > 0;
}

export function wouldExceedKlippekortCap(existingActiveCount, newKlippekortCount, max = 3) {
  return existingActiveCount + newKlippekortCount > max;
}

// Fat-finger guard, not a business ceiling - catches an accidental huge quantity/stack,
// not meant to block a genuinely large group booking under the cap.
export function exceedsOrderCap(totalPrice, max = 5000) {
  return totalPrice > max;
}

// Points for a purchase: exactly `percent` % (park setting, default 4) of the price
// paid, to two decimals (3 kr = 0.12 points) - the same rule the server uses when it
// awards them (backend orderCompletion.js), so the cart shows what the customer gets.
export function pointsForPurchase(paidPrice, percent = 4) {
  return Math.round(Math.max(0, paidPrice) * percent) / 100;
}
